import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Utensils, ShieldCheck, ChefHat, Users as UsersIcon, ArrowRight, Loader2 } from "lucide-react";
import { roleHomePath } from "@/lib/roles";
import { toast } from "sonner";

export default function LoginPage() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [devEmail, setDevEmail] = useState("");
    const [devRole, setDevRole] = useState("customer");
    const [loading, setLoading] = useState(false);

    const startGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const devLogin = async () => {
        if (!devEmail) {
            toast.error("Enter an email");
            return;
        }
        setLoading(true);
        try {
            // Dev login also sets an httpOnly session_token cookie via withCredentials.
            // We intentionally do NOT mirror the token into localStorage (XSS exfiltration risk).
            const { data } = await api.post("/auth/dev-login", {
                email: devEmail,
                role: devRole,
                name: devEmail.split("@")[0],
            });
            setUser({
                user_id: data.user_id,
                email: data.email,
                name: data.name,
                role: data.role,
                picture: `https://api.dicebear.com/9.x/initials/svg?seed=${data.name}`,
            });
            toast.success(`Signed in as ${data.role}`);
            const target = roleHomePath(data.role);
            navigate(target, { replace: true });
        } catch (error) {
            console.warn("[login] dev-login failed", error?.message);
            toast.error("Sign-in failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative warm-linen-wash">
            <div className="min-h-screen grid lg:grid-cols-2">
                <div className="hidden lg:flex relative items-end p-12">
                    <img
                        src="https://images.unsplash.com/photo-1604610728890-6f4b631ed081?w=1400"
                        alt="Warmly lit restaurant interior"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="relative text-white max-w-md">
                        <h2 className="font-display text-5xl leading-tight">Dinner, perfectly choreographed.</h2>
                        <p className="mt-4 text-white/85">From booking to bill — a single graceful flow. ReserveLite turns your night out into something effortless.</p>
                    </div>
                </div>
                <div className="flex items-center justify-center p-6 sm:p-12">
                    <Card className="w-full max-w-md">
                        <CardContent className="p-8">
                            <div className="flex items-center gap-2 mb-6">
                                <div className="h-10 w-10 rounded-xl bg-[hsl(var(--brand-terracotta))] grid place-items-center text-white"><Utensils className="h-5 w-5" /></div>
                                <div>
                                    <div className="font-display text-2xl">ReserveLite</div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Welcome back</div>
                                </div>
                            </div>
                            <h1 className="font-display text-3xl mb-1">Sign in to continue</h1>
                            <p className="text-sm text-muted-foreground mb-6">Reserve, dine, and pay — all from one place.</p>

                            <Button className="w-full h-11" onClick={startGoogle} data-testid="google-signin-button">
                                <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2" aria-hidden>
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.09-1.92 3.28-4.74 3.28-8.07z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
                                </svg>
                                Continue with Google
                            </Button>

                            <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
                                <div className="flex-1 h-px bg-border" />
                                <span>or for demo / testing</span>
                                <div className="flex-1 h-px bg-border" />
                            </div>

                            <Tabs defaultValue="customer" onValueChange={setDevRole} data-testid="dev-role-tabs">
                                <TabsList className="grid grid-cols-4 mb-4">
                                    <TabsTrigger value="customer" data-testid="dev-tab-customer">
                                        <Utensils className="h-3.5 w-3.5 mr-1" /> Diner
                                    </TabsTrigger>
                                    <TabsTrigger value="staff" data-testid="dev-tab-staff">
                                        <UsersIcon className="h-3.5 w-3.5 mr-1" /> Staff
                                    </TabsTrigger>
                                    <TabsTrigger value="kitchen" data-testid="dev-tab-kitchen">
                                        <ChefHat className="h-3.5 w-3.5 mr-1" /> Kitchen
                                    </TabsTrigger>
                                    <TabsTrigger value="admin" data-testid="dev-tab-admin">
                                        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Admin
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                            <div className="space-y-3">
                                <div className="grid gap-1.5">
                                    <Label htmlFor="dev-email">Email</Label>
                                    <Input id="dev-email" placeholder="alice@example.com" value={devEmail}
                                        onChange={(e) => setDevEmail(e.target.value)} data-testid="dev-email-input" />
                                </div>
                                <Button variant="secondary" className="w-full h-10" onClick={devLogin} disabled={loading} data-testid="dev-login-button">
                                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                    Continue as {devRole}
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                                <p className="text-[11px] text-muted-foreground">
                                    Demo bypass for testing the three roles. In production, Google sign-in is used.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
